import React from 'react';
import { Paper, Box, Typography, Chip, IconButton, Tooltip, Stack, Divider, Button } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PhoneIcon from '@mui/icons-material/Phone';
import EventIcon from '@mui/icons-material/Event';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CarIcon from '@mui/icons-material/DirectionsCar';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

import { getStatusChipColor, normalizePreInterviewDurum, arsivDurumlari } from './PreInterviewConstants';
import { sonNotMetni, notSayisi, notTarihiToDate } from './PreInterviewNotes';

const formatTarih = (timestamp) => {
    const date = notTarihiToDate(timestamp);
    if (!date) return '-';
    return date.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const PreInterviewCard = ({ interview, onMenuOpen, onEdit, onCreateWorkOrder }) => {

    // Tanınmayan (v1 kalıntısı) durumlar 'Yeni Görüşme' gibi gösterilir — veri değişmez.
    const durum = normalizePreInterviewDurum(interview.durum);
    // İptal Oldu veya Müşteriye Dönüştü durumlarında buton görünmesin
    const showCreateButton = !arsivDurumlari.includes(durum);

    const notOnizleme = sonNotMetni(interview.notlar);
    const toplamNot = notSayisi(interview.notlar);

    const hatirlatma = notTarihiToDate(interview.hatirlatmaTarihi);
    const hatirlatmaGecti = Boolean(hatirlatma) && hatirlatma.getTime() < Date.now();

    return (
        <Paper elevation={2} sx={{ display: 'flex', flexDirection: 'column', height: '100%', '&:hover': { boxShadow: 6, cursor: 'pointer' } }}>
            <Box
                sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'grey.100' }}
                onClick={() => onEdit(interview)}
            >
                <Box>
                    <Typography variant="h6" component="div" fontWeight="bold">{interview.adSoyad}</Typography>
                    <Chip
                        label={durum}
                        color={getStatusChipColor(durum)}
                        size="small"
                        sx={{ mt: 0.5 }}
                    />
                </Box>
                <IconButton onClick={(e) => { e.stopPropagation(); onMenuOpen(e, interview); }}>
                    <MoreVertIcon />
                </IconButton>
            </Box>

            <Divider />

            <Stack spacing={1.5} sx={{ p: 2 }} onClick={() => onEdit(interview)}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2">{interview.telefon}</Typography>
                </Stack>
                {interview.aracPlaka && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CarIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                            {interview.aracPlaka}
                            {interview.aracMarka && ` - ${interview.aracMarka}`}
                            {interview.aracModel && ` ${interview.aracModel}`}
                        </Typography>
                    </Stack>
                )}
                {interview.aracTuru && !interview.aracPlaka && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CarIcon fontSize="small" color="action" />
                        <Typography variant="body2">{interview.aracTuru}</Typography>
                    </Stack>
                )}
                {interview.plakaBekleniyor && !interview.aracPlaka && (
                    <Box>
                        <Chip label="Plaka Bekleniyor" size="small" color="warning" variant="outlined" />
                    </Box>
                )}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {interview.ilgilenilenHizmetler?.length > 0 ? (
                        interview.ilgilenilenHizmetler.map(h => <Chip key={h} label={h} size="small" variant="outlined" />)
                    ) : (
                        <Chip label="Hizmet Seçilmemiş" size="small" variant="outlined" />
                    )}
                </Box>

                {notOnizleme && (
                    <Tooltip title={notOnizleme}>
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{
                                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>
                                {notOnizleme}
                            </Typography>
                            {toplamNot > 1 && (
                                <Typography variant="caption" color="text.disabled" display="block">
                                    +{toplamNot - 1} not daha
                                </Typography>
                            )}
                        </Box>
                    </Tooltip>
                )}
            </Stack>

            <Box sx={{ flexGrow: 1 }} />

            <Divider />
            <Stack sx={{ p: 2, backgroundColor: 'grey.50' }}>
                <Box sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="bold" color="text.primary">Toplam (KDV hariç):</Typography>
                        <Typography variant="h6" fontWeight="bold" color="primary.main">
                            {(interview.toplamTutar || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                        </Typography>
                    </Box>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <EventIcon fontSize="small" color="action" />
                    <Typography variant="caption">Görüşme: {formatTarih(interview.createdAt || interview.olusturmaTarihi)}</Typography>
                </Stack>

                {hatirlatma && (
                    <Stack
                        direction="row" spacing={1} alignItems="center"
                        sx={{ backgroundColor: hatirlatmaGecti ? 'error.light' : 'info.light', p: 0.5, borderRadius: 1, mb: 1 }}
                    >
                        {hatirlatmaGecti
                            ? <NotificationsActiveIcon fontSize="small" color="error" />
                            : <ScheduleIcon fontSize="small" color="info" />}
                        <Typography variant="caption" fontWeight="bold" color={hatirlatmaGecti ? 'error.dark' : 'info.dark'}>
                            {hatirlatmaGecti ? 'Hatırlatma geçti: ' : 'Hatırlatma: '}
                            {formatTarih(interview.hatirlatmaTarihi)}
                        </Typography>
                    </Stack>
                )}

                {/* Randevu Oluştur Butonu */}
                {showCreateButton && onCreateWorkOrder && (
                    <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<EventIcon />}
                        onClick={(e) => {
                            e.stopPropagation();
                            onCreateWorkOrder(interview);
                        }}
                        sx={{ mt: 1, fontWeight: 'bold' }}
                        fullWidth
                    >
                        Randevu Oluştur
                    </Button>
                )}
            </Stack>
        </Paper>
    );
};

export default PreInterviewCard;